// Best-effort, UNVERIFIED decode of a first-passkey enrollment token's public
// claims, for UI pre-fill ONLY.
//
// The access-email deep-link arrives as /login?enroll=<cesenroll_…> where the
// token is a signed JWT (`cesenroll_<header>.<payload>.<signature>`). The
// passphrase enrollment door needs the bound email to pre-fill its form — but
// the email lives only inside the token, not in the URL query. We read the JWT
// payload here WITHOUT verifying the signature, purely to pre-fill that field.
//
// This is NOT a security boundary:
//   - the real identity is enforced server-side by POST /auth/register +
//     POST /auth/login, which re-derive the account from the typed credentials
//     (a tampered `email` claim just pre-fills a different value the user can
//     see and edit; it grants nothing), and
//   - the post-login redirect target is taken from the already-validated
//     ?return_to= query param, not from this decode.
// Any malformed input yields all-null claims.

export interface EnrollmentTokenClaims {
  email: string | null;
  returnTo: string | null;
  verticalId: string | null;
}

const ENROLLMENT_TOKEN_PREFIX = 'cesenroll_';

function decodeBase64Url(segment: string): string {
  let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const remainder = b64.length % 4;
  if (remainder) b64 += '='.repeat(4 - remainder);
  if (typeof atob === 'function') {
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  // Node fallback (non-browser contexts); harmless if `Buffer` is absent.
  const maybeBuffer = (globalThis as { Buffer?: { from(input: string, enc: string): { toString(enc: string): string } } }).Buffer;
  if (maybeBuffer) return maybeBuffer.from(b64, 'base64').toString('utf-8');
  throw new Error('No base64 decoder available');
}

function asClaimString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function decodeEnrollmentTokenClaims(
  token: string | null | undefined,
): EnrollmentTokenClaims {
  const empty: EnrollmentTokenClaims = { email: null, returnTo: null, verticalId: null };
  if (!token || !token.trim()) return empty;
  const raw = token.trim();
  const jwt = raw.startsWith(ENROLLMENT_TOKEN_PREFIX)
    ? raw.slice(ENROLLMENT_TOKEN_PREFIX.length)
    : raw;
  const segments = jwt.split('.');
  if (segments.length < 2 || !segments[1]) return empty;
  try {
    const parsed = JSON.parse(decodeBase64Url(segments[1])) as Record<string, unknown>;
    return {
      email: asClaimString(parsed.email) ?? asClaimString(parsed.sub),
      returnTo: asClaimString(parsed.return_to),
      verticalId: asClaimString(parsed.vertical_id),
    };
  } catch {
    return empty;
  }
}
